/**
 * useIndexedDBBackup.ts
 * IndexedDB utilities for backing up voice recording chunks
 * Prevents data loss during long recordings (1-1.5 hours)
 */

const DB_NAME = 'VoiceRecordingBackup';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';

export interface RecordingChunk {
  sessionId: string;
  chunkIndex: number;
  blob: Blob;
  timestamp: number;
  uploaded: boolean;
  retryCount: number;
}

export interface RecordingSession {
  sessionId: string;
  startTime: number;
  lastChunkIndex: number;
  totalChunks: number;
  finalized: boolean;
  patientId?: string; // Associate recording with patient
}

/**
 * Initialize IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Chunks store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: ['sessionId', 'chunkIndex'],
        });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('uploaded', 'uploaded', { unique: false });
      }

      // Sessions store (metadata)
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions', {
          keyPath: 'sessionId',
        });
      }
    };
  });
}

/**
 * Save a chunk to IndexedDB
 */
export async function saveChunk(chunk: RecordingChunk): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(chunk);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Mark a chunk as uploaded
 */
export async function markChunkUploaded(
  sessionId: string,
  chunkIndex: number
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get([sessionId, chunkIndex]);

    request.onsuccess = () => {
      const chunk = request.result;
      if (chunk) {
        chunk.uploaded = true;
        store.put(chunk);
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all chunks for a session
 */
export async function getSessionChunks(
  sessionId: string
): Promise<RecordingChunk[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('sessionId');
    const request = index.getAll(sessionId);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get chunks that need uploading
 */
export async function getPendingChunks(
  sessionId: string
): Promise<RecordingChunk[]> {
  const chunks = await getSessionChunks(sessionId);
  return chunks.filter((c) => !c.uploaded).sort((a, b) => a.chunkIndex - b.chunkIndex);
}

/**
 * Save session metadata
 */
export async function saveSession(session: RecordingSession): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['sessions'], 'readwrite');
    const store = transaction.objectStore('sessions');
    const request = store.put(session);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get unfinalized sessions (for recovery)
 */
export async function getOrphanedSessions(): Promise<RecordingSession[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['sessions'], 'readonly');
    const store = transaction.objectStore('sessions');
    const request = store.getAll();

    request.onsuccess = () => {
      const allSessions = request.result || [];
      const now = Date.now();
      // Filter unfinalized sessions that are older than 5 minutes
      const orphaned = allSessions.filter(
        (s) => !s.finalized && now - s.startTime > 5 * 60 * 1000
      );
      resolve(orphaned);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Mark session as finalized (uploaded + transcribed)
 */
export async function finalizeSession(sessionId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['sessions'], 'readwrite');
    const store = transaction.objectStore('sessions');
    const request = store.get(sessionId);

    request.onsuccess = () => {
      const session = request.result;
      if (session) {
        session.finalized = true;
        store.put(session);
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete session and all its chunks
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const db = await openDB();
  
  // Delete chunks
  const chunksTransaction = db.transaction([STORE_NAME], 'readwrite');
  const chunksStore = chunksTransaction.objectStore(STORE_NAME);
  const index = chunksStore.index('sessionId');
  const chunks = await new Promise<RecordingChunk[]>((resolve, reject) => {
    const request = index.getAll(sessionId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });

  for (const chunk of chunks) {
    chunksStore.delete([chunk.sessionId, chunk.chunkIndex]);
  }

  // Delete session metadata
  const sessionTransaction = db.transaction(['sessions'], 'readwrite');
  const sessionStore = sessionTransaction.objectStore('sessions');
  sessionStore.delete(sessionId);
}

/**
 * Clean up old finalized sessions (> 7 days)
 */
export async function cleanupOldSessions(): Promise<void> {
  const db = await openDB();
  const transaction = db.transaction(['sessions'], 'readwrite');
  const store = transaction.objectStore('sessions');
  const request = store.getAll();

  request.onsuccess = async () => {
    const sessions = request.result || [];
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000; // 7 days ago
    
    for (const session of sessions) {
      if (session.finalized && session.startTime < cutoff) {
        await deleteSession(session.sessionId);
      }
    }
  };
}
